import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Download, Bell, Smartphone, Zap } from "lucide-react";
import { requestNotificationPermission } from "@/lib/onesignal";
import { toast } from "sonner";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const Install = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      toast.info("To install on iOS: Tap the Share button, then 'Add to Home Screen'");
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === "accepted") {
      setIsInstalled(true);
      toast.success("App installed successfully!");
    }

    setDeferredPrompt(null);
  };

  const handleEnableNotifications = async () => {
    const granted = await requestNotificationPermission();
    if (granted) {
      toast.success("Notifications enabled! You'll receive updates for receipts and tax reminders.");
    } else {
      toast.error("Notifications permission denied. You can enable it later in browser settings.");
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Install Tax.GBT</h1>
        <p className="text-muted-foreground">
          Get the best experience with our Progressive Web App
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 mb-8">
        <Card>
          <CardHeader>
            <Download className="w-8 h-8 text-primary mb-2" />
            <CardTitle>Install App</CardTitle>
            <CardDescription>
              Add Tax.GBT to your home screen for quick access
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleInstallClick} className="w-full" disabled={isInstalled}>
              {isInstalled ? "Already Installed" : "Install Now"}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <Bell className="w-8 h-8 text-primary mb-2" />
            <CardTitle>Enable Notifications</CardTitle>
            <CardDescription>
              Get notified about receipts, reports, and tax deadlines
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleEnableNotifications} className="w-full" variant="secondary">
              Enable Notifications
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Why Install?</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="flex flex-col items-center text-center p-4">
              <Smartphone className="w-10 h-10 text-primary mb-3" />
              <h3 className="font-semibold mb-2">Home Screen Access</h3>
              <p className="text-sm text-muted-foreground">
                Launch directly from your home screen like a native app
              </p>
            </div>
            <div className="flex flex-col items-center text-center p-4">
              <Zap className="w-10 h-10 text-primary mb-3" />
              <h3 className="font-semibold mb-2">Lightning Fast</h3>
              <p className="text-sm text-muted-foreground">
                Instant loading and smooth performance
              </p>
            </div>
            <div className="flex flex-col items-center text-center p-4">
              <Bell className="w-10 h-10 text-primary mb-3" />
              <h3 className="font-semibold mb-2">Stay Updated</h3>
              <p className="text-sm text-muted-foreground">
                Get push notifications for important updates
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Installation Instructions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">On Chrome (Desktop & Android):</h4>
              <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                <li>Click the "Install" button above</li>
                <li>Confirm the installation in the browser prompt</li>
                <li>The app will be added to your device</li>
              </ol>
            </div>
            <div>
              <h4 className="font-semibold mb-2">On Safari (iOS):</h4>
              <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                <li>Tap the Share button (square with arrow)</li>
                <li>Scroll down and tap "Add to Home Screen"</li>
                <li>Tap "Add" to confirm</li>
              </ol>
            </div>
            <div>
              <h4 className="font-semibold mb-2">On Firefox & Edge:</h4>
              <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                <li>Click the "Install" button above</li>
                <li>Or look for the install icon in the address bar</li>
                <li>Follow the browser prompts</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Install;
